import { isShelved } from "@lms/shared";

/**
 * Le decisioni della schermata allarmi che si possono provare da sole.
 *
 * Stanno fuori dal componente perche' sono quelle che sbagliano in silenzio:
 * un ordinamento storto mette in fondo l'allarme da guardare, una chiave i18n
 * costruita male mostra testo grezzo, un fuso interpretato al contrario
 * sposta ogni orario di due ore. Nessuna di queste tre cose fa fallire niente.
 */

export interface AlarmLike {
  state: string;
  createdAt: string;
}

/**
 * Stato -> chiave i18n.
 *
 * I nomi sono quelli che il C++ scrive in tabella (`ACKED`, `SHLVD`), e le
 * chiavi di traduzione hanno gli stessi: chi concatena `"alarm.state." +
 * stato` funziona, ed e' quello che fa l'app desktop. La mappa resta perche'
 * copre anche le due grafie che questo server ha scritto prima
 * dell'allineamento, e perche' `NORM`, `DSUPR` e `OOSRV` una traduzione non
 * ce l'hanno: li' si mostra lo stato grezzo, che e' meglio di una chiave
 * inventata vista come testo crudo dall'operatore.
 */
export const STATE_KEY: Record<string, string> = {
  UNACK: "alarm.state.UNACK",
  ACKED: "alarm.state.ACKED",
  RTNUN: "alarm.state.RTNUN",
  SHLVD: "alarm.state.SHLVD",
  ACK: "alarm.state.ACKED",
  SHELVED: "alarm.state.SHLVD",
};

export function stateLabel(state: string, t: (k: string) => string): string {
  const key = STATE_KEY[state];
  return key ? t(key) : state;
}

/** Stati in cui l'allarme aspetta ancora una mano. */
export const NEEDS_ACK = new Set(["UNACK", "RTNUN"]);

/**
 * Chi aspetta prima, silenziati in fondo, e a parita' il piu' recente prima.
 *
 * I silenziati **restano nella lista**: un allarme sparito dalla vista e' un
 * allarme che nessuno rivede, e la sua scadenza arriva senza che nessuno la
 * aspetti.
 */
export function sortAlarms<T extends AlarmLike>(rows: readonly T[]): T[] {
  const rank = (s: string) => (NEEDS_ACK.has(s) ? 0 : isShelved(s) ? 2 : 1);
  return [...rows].sort(
    (a, b) => rank(a.state) - rank(b.state) || b.createdAt.localeCompare(a.createdAt),
  );
}

/**
 * Data e ora locali da un timestamp del server.
 *
 * Il fuso si IMPONE quando manca: il server scrive in UTC, e un ISO senza `Z`
 * verrebbe letto come ora locale — ogni orario sfalsato di tutto l'offset del
 * fuso, due ore qui d'estate, senza che niente lo segnali.
 */
export function formatWhen(iso: string): string {
  const ms = Date.parse(/[Zz]$|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
  if (!Number.isFinite(ms)) return iso;
  return new Date(ms).toLocaleString();
}
